import * as THREE from 'three'
import { ImportedModelObjectMetadataService } from '../../metadata'

/**
 * 导入模型实例组键工厂。
 *
 * 键只描述当前运行时可以共享同一个 InstancedMesh 的渲染状态；其中 Geometry/Material
 * UUID 仅作为运行时资源身份，不会进入装配文档、Undo/Redo 或拓扑引用。
 */
export class ImportedModelInstancedMeshKeyFactory {
  /** 用于计算 occurrence 局部矩阵的复用临时矩阵。 */
  private readonly inverseOccurrenceMatrix = new THREE.Matrix4()

  /** 用于计算 occurrence 局部矩阵的复用临时矩阵。 */
  private readonly slotLocalMatrix = new THREE.Matrix4()

  /**
   * 为一个 source Mesh 创建定义内渲染槽键。
   *
   * @param definitionKey 当前共享定义身份。
   * @param occurrenceRoot 当前 occurrence 的权威变换根。
   * @param sourceMesh 当前定义中的原始显示 Mesh。
   * @returns 同定义、同 Geometry、同材质、同局部位姿才相等的运行时键。
   */
  public create(
    definitionKey: string,
    occurrenceRoot: THREE.Object3D,
    sourceMesh: THREE.Mesh,
  ): string {
    occurrenceRoot.updateWorldMatrix(true, false)
    sourceMesh.updateWorldMatrix(true, false)
    this.inverseOccurrenceMatrix.copy(occurrenceRoot.matrixWorld).invert()
    this.slotLocalMatrix.multiplyMatrices(
      this.inverseOccurrenceMatrix,
      sourceMesh.matrixWorld,
    )

    const displaySource = ImportedModelObjectMetadataService.peekOwnMetadata(sourceMesh)?.displaySource
    const materialKey = (Array.isArray(sourceMesh.material)
      ? sourceMesh.material
      : [sourceMesh.material])
      .map(material => material.uuid)
      .join(',')
    const displaySlotKey = displaySource
      ? `${displaySource.meshIndex ?? 'none'}:${displaySource.primitiveIndex}`
      : 'runtime'
    const localMatrixKey = this.slotLocalMatrix.elements
      .map(value => Math.round(value * 1e8) / 1e8)
      .join(',')

    return [
      definitionKey,
      displaySlotKey,
      sourceMesh.geometry.uuid,
      materialKey,
      localMatrixKey,
      sourceMesh.renderOrder,
      sourceMesh.layers.mask,
      sourceMesh.castShadow ? 1 : 0,
      sourceMesh.receiveShadow ? 1 : 0,
    ].join('|')
  }
}
